import TableFactory from "../components/TableFactory";
import React, {useEffect} from "react";
import {SalesPerson} from "sharedlibs";
import {LMTableApi, PaginationModel, SortModel} from "../components/Tables/types";
import {salesPersonTableColumns} from "../constants";
import PageTitle from "../components/subcomponents/PageTitle";
import {useTranslation} from "react-i18next";
import {Box, Button, Divider} from "@mui/material";
import {TablePaper} from "../components/TablePaper";
import AddButton from "../components/subcomponents/Buttons/AddButton";
import {ButtonContainer} from "../components/subcomponents/Containers/Containers";
import ButtonWithMargin from "../components/subcomponents/Buttons/ButtonWithMargin";
import TableSearch from "../components/subcomponents/TableSearch";
import {applySearchFilter, sortBySortModel} from "../utils/helperFunctions";
import {useNavigate} from "react-router-dom";
import SalesPersonService from "../services/SalesPersonService";
import {useUserRole} from "../utils/UserRoleContext";
import {logBusinessAudit} from "../utils/auditLogHelper";

interface SalesPersonPageProps {
}

export const SalesPersonPage: React.FC<SalesPersonPageProps> = ({}) => {

  const [isLoading, setLoading] = React.useState<boolean>(false)
  const [salesPersons, setSalesPersons] = React.useState<SalesPerson[]>([])
  const [selectedRowIds, setSelectedRowIds] = React.useState<string[]>([]);
  const [searchFilter, setSearchFilter] = React.useState<string>('')
  const {isAdmin} = useUserRole();

  const apiRef = React.useRef<LMTableApi | null>(null);

  const {t} = useTranslation()
  const navigate = useNavigate()

  const [pagination, setPagination] = React.useState<PaginationModel>({page: 0, pageSize: 15});
  const [sortModel, setSortModel] = React.useState<SortModel>([
    {field: 'lastName', sort: 'asc'}
  ]);

  function handleSelectionChange(selectionModel: any) {
    if (selectionModel.length > 1) {
      setSelectedRowIds(selectionModel)
    } else {
      setSelectedRowIds(selectionModel.splice(-1))
    }
  }

  const filteredItems = React.useMemo(
    () => applySearchFilter(salesPersons, searchFilter),
    [salesPersons, searchFilter]
  )

  // Sorted before slicing: this page hands LMTable a single page, which the table
  // can only order within — without this, clicking a column header would reorder
  // the visible 15 rows while every other page kept its original partition.
  const sortedItems = React.useMemo(
    () => sortBySortModel(filteredItems, sortModel, salesPersonTableColumns),
    [filteredItems, sortModel]
  )

  const pagedItems = React.useMemo(() => {
    const start = pagination.page * pagination.pageSize
    return sortedItems.slice(start, start + pagination.pageSize)
  }, [sortedItems, pagination])

  async function loadData() {
    const salesPersons = await SalesPersonService.getAllSalesPersons()
    setSalesPersons(salesPersons)
  }

  useEffect(() => {
    loadData().then()
  }, []);

  function renderTable() {
    return (
      <div>
        {
          TableFactory.createTable(
            'salesperson',
            salesPersonTableColumns.map(elem => elem.field),
            pagedItems,
            selectedRowIds,
            handleSelectionChange,
            (model) => {
              setSortModel(model)
            },
            {sortModel},
            apiRef,
            pagination,
            setPagination,
            filteredItems.length,
            isLoading
          )
        }
      </div>
    )
  }

  function handleAddClick(): void {
    navigate("/salesperson/add?fallbackURL=salesperson")
  }

  async function handleUpdate() {
    navigate(`/salesperson/update/${selectedRowIds[0]}?fallbackURL=salesperson`)
  }

  async function handleDelete() {
    if (selectedRowIds.length !== 1) {
      return
    }

    const deletedPerson = salesPersons.find(sp => sp.id === selectedRowIds[0])
    await SalesPersonService.deleteSalesPerson(selectedRowIds[0])
    await logBusinessAudit('audit.salesPerson.deleted', '', deletedPerson ?? null, null)
    await loadData()
  }

  return (
    <div className={"base-container"}>
      <PageTitle>{t('appBar.salesperson')}</PageTitle>
      <Divider/>

      <TablePaper>
        <Box>
          <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                width: '100%',
                gap: 2,
                marginTop: 0,
                paddingTop: 0,
                paddingBottom: 2,
                paddingLeft: 2,
                paddingRight: 2,
                flexFlow: 'row wrap'
              }}>
            <Box sx={{display: 'flex', flexWrap: 'unset', flexDirection: 'column', width: 'calc(50% - 8px)'}}>
              <div style={{display: 'flex', justifyContent: 'left'}}>
                <TableSearch onSearch={(input) => {
                  setSearchFilter(input)
                }}/>
              </div>
            </Box>

            <Box sx={{display: 'flex', flexWrap: 'unset', flexDirection: 'column', width: 'calc(50% - 8px)'}}>
              <div style={{display: 'flex', justifyContent: 'right'}}>
                <AddButton onClick={handleAddClick} disabled={!isAdmin}/>
              </div>
            </Box>
          </Box>

          <Box sx={{paddingBottom: 2}}>
            <div>
              {renderTable()}
            </div>
          </Box>

          <ButtonContainer>
            <ButtonWithMargin
              variant="contained"
              color="secondary"
              disabled={selectedRowIds.length !== 1 || !isAdmin}
              onClick={handleDelete}
            >
              {t('activities.delete')}
            </ButtonWithMargin>
            <Button
              variant="contained"
              disabled={selectedRowIds.length !== 1}
              onClick={handleUpdate}
            >
              {isAdmin ? t('activities.edit') : t('activities.view')}
            </Button>
          </ButtonContainer>
        </Box>
      </TablePaper>
    </div>
  )
}

